import React, { useState } from "react";
import * as XLSX from "xlsx";
import { saveExcelData, fetchDuplicateInvoices } from "./Transactions";
import { CallStates } from "../../../components/SharedComponents/AppStates/Utils";

const DuplicateInvoiceStep = ({ api, failedRows, setFailedRows, setCurrentStep }) => {
  // failedRows here is already the confirmed duplicate set returned by
  // GetDuplicateInvoiceList (backend has marked these rows Status = "Repeated").
  const duplicateRows = failedRows;

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [callState, setCallState] = useState(CallStates.INITIAL);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Same delete flow as Failed Invoices - InvoicePaymentScheduler with
  // module: "Delete" - just re-fetches the duplicate list afterwards instead
  // of the failed list, since that's the screen we're on.
  const handleDeleteRow = (row) => {
    setErrorMessage(null);
    setDeletingId(row.InvoiceListBulkPaymentID);
    const summary = { invoicelist: [row], module: "Delete" };

    saveExcelData(api, summary, setCallState)
      .then(() => fetchDuplicateInvoices(api))
      .then((response) => {
        setFailedRows(response.InvoiceListItems || []);
      })
      .catch(() => {
        setErrorMessage("Failed to delete selected invoice.");
      })
      .finally(() => {
        setDeletingId(null);
      });
  };

  const filteredRows = duplicateRows.filter((row) => {
    if (!row.InsertedDate) {
      return true;
    }
    const rowDate = new Date(row.InsertedDate);
    if (dateFrom && rowDate < new Date(dateFrom)) {
      return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (rowDate > to) {
        return false;
      }
    }
    return true;
  });

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "";
    }
    const parsed = new Date(dateValue);
    return isNaN(parsed) ? "" : parsed.toLocaleDateString();
  };

  const formatAmount = (amount) => `£${Number(amount || 0).toFixed(2)}`;

  // Same "Payment collection template" format as Failed Invoices' export.
  const handleDownloadExcel = () => {
    if (filteredRows.length === 0) {
      setErrorMessage("No invoices to download.");
      return;
    }
    const worksheetData = filteredRows.map((row) => ({
      "Healthcode / Invoice No": row.InvoiceId,
      "Amount to collect": row.Amount,
      Status: row.Status,
      Date: formatDate(row.InsertedDate),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payment collection");
    XLSX.writeFile(workbook, "Payment collection template.xlsx");
  };

  // Group by InvoiceId (oldest first within a group) so the rows sharing the
  // same Invoice ID sit next to each other and the duplication is obvious at
  // a glance, rather than scattered through the list in upload order.
  const sortedRows = [...filteredRows].sort((a, b) => {
    if (a.InvoiceId !== b.InvoiceId) {
      return a.InvoiceId - b.InvoiceId;
    }
    return new Date(a.InsertedDate || 0) - new Date(b.InsertedDate || 0);
  });

  let lastInvoiceId = null;
  let shaded = false;
  const rowsWithGroup = sortedRows.map((row) => {
    const isNewGroup = row.InvoiceId !== lastInvoiceId;
    if (isNewGroup) {
      shaded = !shaded;
      lastInvoiceId = row.InvoiceId;
    }
    return { row, isNewGroup, shaded };
  });

  return (
    <div className="failed-invoices-container">
      <div className="failed-invoices-header">
        <div>
          <h2>Duplicate Invoices</h2>
          <p className="failed-invoices-subtitle">
            Invoice Numbers that appear more than once in an uploaded sheet -
            grouped together below, marked as "Repeated".
          </p>
          <p className="invoice-user-note">
            Please delete the repeated invoices before uploading the same
            invoice ID again.
          </p>
        </div>
        <a
          href="#"
          className="invoice-table-link"
          onClick={(e) => {
            e.preventDefault();
            setCurrentStep("upload");
          }}
        >
          ← Go to Upload Page
        </a>
      </div>

      {duplicateRows.length > 0 ? (
        <div>
          <div className="invoice-toolbar">
            <div className="invoice-toolbar-summary">
              <span className="invoice-toolbar-count">
                {filteredRows.length}
              </span>
              <span>
                {filteredRows.length === duplicateRows.length
                  ? "duplicate invoices"
                  : `of ${duplicateRows.length} duplicate invoices`}
              </span>
            </div>
            <div className="invoice-filter-bar">
              <label>
                From
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="invoice-link-button"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {filteredRows.length === 0 ? (
            <div className="invoice-card-empty">
              No duplicate invoices match the selected filters.
            </div>
          ) : (
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Case ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rowsWithGroup.map(({ row, isNewGroup, shaded: rowShaded }) => (
                  <tr
                    key={row.InvoiceListBulkPaymentID}
                    className={
                      "invoice-table-row" +
                      (rowShaded ? " invoice-table-row-shaded" : "") +
                      (isNewGroup ? " invoice-table-row-group-start" : "")
                    }
                  >
                    <td>{row.InvoiceId}</td>
                    <td>{row.CaseId ?? ""}</td>
                    <td>{formatAmount(row.Amount)}</td>
                    <td>
                      <span className="invoice-status-badge invoice-status-warning">
                        {row.Status}
                      </span>
                    </td>
                    <td>{formatDate(row.InsertedDate)}</td>
                    <td>
                      <button
                        className="invoice-card-delete"
                        type="button"
                        disabled={callState === CallStates.SAVING}
                        onClick={() => handleDeleteRow(row)}
                        aria-label={`Delete invoice ${row.InvoiceId}`}
                      >
                        {deletingId === row.InvoiceListBulkPaymentID
                          ? "Deleting..."
                          : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="retry-button-container">
            <button
              className="invoice-table-button invoice-table-button-secondary"
              type="button"
              disabled={callState === CallStates.SAVING}
              onClick={handleDownloadExcel}
            >
              Download as Excel
            </button>
          </div>

          {errorMessage && (
            <div className="invoice-bulk-error-message">{errorMessage}</div>
          )}
        </div>
      ) : (
        <div className="invoice-bulk-message-success">
          <p>No duplicate invoices found.</p>
        </div>
      )}
    </div>
  );
};

export default DuplicateInvoiceStep;
